<template>
  <!-- 费用统计：按费用类型 / 人员 / 部门三个维度统计，并对照预算执行 -->
  <report-layout active="expense">
    <div class="expense-stat-report">
    <list-page>
      <search-condition @search="loadReport" :show-search="true" page-name="expenseStatReport">
        <template #default>
          <!-- 维度切换 -->
          <div class="dim-bar">
            <span class="dim-label">统计维度</span>
            <el-radio-group v-model="query.groupType" @change="loadReport">
              <!-- 用 value 而不是 label：label 当值在 Element Plus 3.0 会弃用 -->
              <el-radio-button v-for="d in DIMENSIONS" :key="d.value" :value="d.value">{{ d.label }}</el-radio-button>
            </el-radio-group>
          </div>
          <el-select v-model="query.budgetYear" placeholder="全部" clearable @change="loadReport">
            <template #prefix>年度</template>
            <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y"/>
          </el-select>
          <depart-select v-model="query.departCode" :data="departTree" placeholder="全部部门" @change="loadReport"/>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadReport">查询</page-button>
        </template>
      </search-condition>
      <!-- 口径说明 -->
      <div class="tip-bar">
        口径说明：金额均取「已通过」的费用报销单，审批中 / 已驳回不计入发生额。
        <template v-if="!isTypeDim">人员与部门维度会带出该对象的预算执行对照，未编制预算的显示「未编制预算」。</template>
      </div>
      <!-- KPI -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="label">费用总额</div>
          <div class="value money">{{ money(report.totalAmount) }}</div>
        </div>
        <div class="stat-card">
          <div class="label">报销单据数</div>
          <div class="value">{{ report.claimCount || 0 }}<span class="unit">单</span></div>
        </div>
        <div class="stat-card">
          <div class="label">超支对象</div>
          <div class="value" style="color:#e5484d">{{ report.overCount || 0 }}<span class="unit">个</span></div>
          <div class="sub">{{ isTypeDim ? '费用类型维度不比对预算' : '仅统计已编制预算的对象' }}</div>
        </div>
      </div>
      <!-- 构成环形图 + 金额排行 -->
      <div class="grid-2">
        <div class="chart-card">
          <div class="section-title">费用构成 · {{ currentDimLabel }}</div>
          <div ref="donutChart" class="chart-div"></div>
        </div>
        <div class="chart-card">
          <div class="section-title">{{ currentDimLabel }}金额排行</div>
          <div ref="rankChart" class="chart-div"></div>
        </div>
      </div>

      <list-table v-loading="loading" background-color="#FFF">
        <yq-table :data="report.rows || []" :empty-text="'暂无数据'" height="100%">
          <yq-table-column :label="currentDimLabel" min-width="150rem">
            <template v-slot="{row}"><span class="cell-title">{{ rowTitle(row) }}</span></template>
          </yq-table-column>
          <yq-table-column label="发生金额" prop="totalAmount" align="right" width="140rem">
            <template v-slot="{row}"><span class="num bold">{{ money(row.totalAmount) }}</span></template>
          </yq-table-column>
          <yq-table-column label="金额占比" prop="amountRatio" min-width="160rem">
            <template v-slot="{row}">
              <div class="ratio-cell">
                <span class="ratio-track">
                  <i :style="{width: Math.min(100, Number(row.amountRatio || 0)) + '%', background: '#1677ff'}"/>
                </span>
                <span class="ratio-text">{{ row.amountRatio || 0 }}%</span>
              </div>
            </template>
          </yq-table-column>
          <yq-table-column label="单据数" prop="claimCount" align="right" width="86rem">
            <template v-slot="{row}"><span class="num">{{ row.claimCount || 0 }}</span></template>
          </yq-table-column>
          <yq-table-column label="单均金额" prop="avgAmount" align="right" width="130rem">
            <template v-slot="{row}"><span class="num">{{ money(row.avgAmount) }}</span></template>
          </yq-table-column>
          <yq-table-column label="预算金额" prop="budgetAmount" align="right" width="140rem">
            <template v-slot="{row}">
              <span v-if="row.budgetAmount === null || row.budgetAmount === undefined" class="muted">—</span>
              <span v-else class="num">{{ money(row.budgetAmount) }}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="预算执行" prop="usedRate" min-width="170rem">
            <template v-slot="{row}">
              <div class="budg-bar" v-if="row.usedRate !== null && row.usedRate !== undefined"
                   v-html="budgetBar(row)"></div>
              <span v-else class="muted">{{ isTypeDim ? '—' : '未编制预算' }}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="状态" prop="budgetStatus" align="center" width="90rem">
            <template v-slot="{row}">
              <el-tag v-if="row.budgetStatus" size="small" :type="statusTagType(row.budgetStatus)">
                {{ row.budgetStatus }}
              </el-tag>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
    </list-page>
    </div>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import DepartSelect from "@/components/h5/data/DepartSelect.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import expenseStatApi from "@/api/dz-crm/expense/expenseStatApi";
import commonApi from "@/api/commonApi";
import {useRoute, useRouter} from "vue-router";

/** 三个统计维度，与后端 ExpenseStatServiceImpl 的白名单对齐 */
const DIMENSIONS = [
  {value: "TYPE", label: "按费用类型"},
  {value: "STAFF", label: "按人员"},
  {value: "DEPT", label: "按部门"},
];
const STATUS_TAG = {"正常": "", "预警": "warning", "超支": "danger"};

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ExpenseStatReport",
  components: {ReportLayout, YqTable, ListPage, ListTable, SearchCondition, PageButton, DepartSelect},
  data() {
    const thisYear = new Date().getFullYear();
    return {
      DIMENSIONS,
      loading: false,
      query: {groupType: "TYPE", budgetYear: String(thisYear), departCode: ""},
      report: {},
      typeList: [],
      departTree: [],
      dictReady: false,
      dataReady: false,
      yearOptions: [String(thisYear), String(thisYear - 1)],
      donutChart: null,
      rankChart: null,
    };
  },
  computed: {
    currentDim() {
      return DIMENSIONS.find(d => d.value === this.query.groupType) || DIMENSIONS[0];
    },
    currentDimLabel() {
      return this.currentDim.label.replace("按", "");
    },
    /** 费用类型维度没有预算口径，明细里预算那几列要显示「—」而不是「未编制预算」 */
    isTypeDim() {
      return this.query.groupType === "TYPE";
    },
  },
  beforeRouteLeave(to, from, next) {
    // Util.cachePageData(this, to, from);
    next();
  },
  mounted() {
    // 费用类型一致性字典：类型名由前端关联，后端只返回 dimKey
    commonApi.queryUniqueDictionaryList("EXPENSE_TYPE").then(list => {
      this.typeList = list || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    commonApi.queryDepartList().then(list => this.departTree = list || []);
    this.loadReport();
  },
  unmounted() {
    [this.donutChart, this.rankChart].forEach(c => c && c.dispose());
  },
  methods: {
    async loadReport() {
      this.loading = true;
      try {
        const params = {};
        Object.keys(this.query).forEach(k => {
          const v = this.query[k];
          if (v === "" || v === null || v === undefined) {
            return;
          }
          params[k] = v;
        });
        this.report = await expenseStatApi.report(params) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    /** 字典与数据都到位才画，避免类型名渲染成空 */
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      const rows = this.report.rows || [];
      const items = rows.map(r => ({
        name: this.rowTitle(r),
        value: Number(r.totalAmount || 0),
      }));

      this.donutChart = this.renderDonut(this.donutChart, this.$refs.donutChart, items, this.report.totalAmount);
      // 排行用横向柱图，类目多时按金额降序取前 10
      const top = items.slice(0, 10);
      this.rankChart = this.renderHBar(this.rankChart, this.$refs.rankChart,
          top.map(i => i.name), top.map(i => i.value));
    },
    renderDonut(chart, dom, items, total) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} 元（{d}%）"},
        legend: {bottom: 0, type: "scroll"},
        series: [{
          type: "pie", radius: ["45%", "68%"], center: ["50%", "45%"],
          label: {formatter: "{b}\n{c} 元"},
          data: items,
        }],
      }, true);
      return chart;
    },
    renderHBar(chart, dom, names, data) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "8%", bottom: "3%", top: "5%", containLabel: true},
        xAxis: {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        yAxis: {type: "category", data: names, axisLabel: {color: "#909399", interval: 0}},
        series: [{type: "bar", barWidth: "50%", itemStyle: {color: "#1677ff"}, data: data}],
      }, true);
      return chart;
    },
    /** 明细行标题：费用类型维度走字典关联，人员/部门维度后端已装好 dimTitle */
    rowTitle(row) {
      if (this.isTypeDim) {
        const t = this.typeList.find(x => x.userCode === row.dimKey);
        return t ? t.dictName : "";
      }
      return row.dimTitle || row.dimKey || "未填写";
    },
    budgetBar(row) {
      const rate = Number(row.usedRate || 0);
      const clamped = Math.max(0, Math.min(100, rate));
      const cls = rate > 100 ? "over" : rate >= 85 ? "warn" : "";
      return '<div class="bar-wrap"><span class="bar-track"><i class="' + cls +
          '" style="width:' + clamped.toFixed(1) + '%"></i></span>' +
          '<span class="bar-pct">' + rate.toFixed(1) + '%</span></div>';
    },
    statusTagType(status) {
      const type = STATUS_TAG[status];
      return type === "" ? undefined : type;
    },
    money(value) {
      if (value === null || value === undefined) {
        return "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
  },
};
</script>

<style lang="scss" scoped>
.expense-stat-report {
  height: 100%;
}

.dim-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  width: 320px;
  .dim-label {
    font-size: 13px;
    color: #606266;
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money {
        font-size: 20px;
      }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
  }

  .chart-div {
    height: 300px;
    width: 100%;
  }
}

.ratio-cell {
  display: flex;
  align-items: center;
  gap: 10px;

  .ratio-track {
    flex: 1;
    height: 6px;
    background: #f0f2f5;
    border-radius: 3px;
    overflow: hidden;

    i {
      display: block;
      height: 100%;
      border-radius: 3px;
      transition: width .3s;
    }
  }

  .ratio-text {
    flex: 0 0 52px;
    text-align: right;
    color: #606266;
    font-variant-numeric: tabular-nums;
  }
}

/* 预算执行进度条：内联 HTML 渲染，样式要穿透 scoped */
.budg-bar {
  :deep(.bar-wrap) {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  :deep(.bar-track) {
    flex: 1;
    height: 6px;
    background: #f0f2f5;
    border-radius: 3px;
    overflow: hidden;
    display: block;
  }

  :deep(.bar-track i) {
    display: block;
    height: 100%;
    border-radius: 3px;
    background: #1677ff;
    transition: width .3s;

    &.warn {
      background: #f09819;
    }

    &.over {
      background: #e5484d;
    }
  }

  :deep(.bar-pct) {
    flex: 0 0 52px;
    text-align: right;
    color: #606266;
    font-variant-numeric: tabular-nums;
  }
}

.cell-title {
  color: #303133;
  font-weight: 500;
}

.num {
  font-variant-numeric: tabular-nums;
}

.muted {
  color: #909399;
}

.bold {
  font-weight: 600;
  color: #303133;
}
</style>
